import React from 'react';
import PropTypes from 'prop-types';
import { Query } from '@apollo/client/react/components';
import Styled from 'styled';
import { mapAfter } from 'apollo-relay/update-query';
import Helmet from 'helmet/helmet';
import { FormattedMessage } from 'react-intl';

import ScrollToTop from 'lib/scroll/scroll-to-top';
import LoadingProgress from 'lib/react-router/loading-progress';
import { RouteWithSearchRenderProps } from 'lib/react-router/route-with-search';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';
import observe from 'lib/intersection-observer/observe';

import { Site } from 'four-nets/site';
import ErrorPage from 'four-nets/errors/error-page';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import Masonry, { MediaKey } from 'four-nets/photoblog/masonry/masonry';
import gaTrack from 'four-nets/lib/google-analytics/track';
import OffsetPager from 'four-nets/ui-v2/pager/offset-pager';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import { RelatedInspiration } from 'four-nets/forum/related-content/related-inspirations';

import relatedContentQuery, { TagSlugTagSlugsQuery, TagSlugTagSlugsQueryVariables } from './related-content.graphql';
import InspirationInfo from './inspiration-info/inspiration-info';
import query, { InspirationQuery, InspirationQueryVariables } from './inspiration.graphql';
import defaultStyles from './inspiration.scss';

const columnsSizes: Map<MediaKey, number> = new Map([
    ['sm', 2],
    ['md', 3],
    ['lg', 4],
]);

interface Params {
    slug: string;
}

interface Search {
    offset?: number;
}

interface Props extends RouteWithSearchRenderProps<Params, Search> {
    styles: typeof defaultStyles;
}

interface State {
    loading?: boolean;
    visible: boolean;
    autoLoadActive: boolean;
}

const FIRST = 32;

class Inspiration extends React.PureComponent<Props, State> {
    fetchMore?: () => void = undefined;
    unobserve?: () => void;
    ref: React.RefObject<HTMLDivElement> = React.createRef();
    state = { visible: false, autoLoadActive: false };

    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    componentDidMount() {
        const element = this.ref && (this.ref.current as Element | null);
        if (element) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '300px' }
            );
        }
    }

    componentWillUnmount() {
        if (this.unobserve) {
            this.unobserve();
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const element = this.ref && (this.ref.current as Element | null);
        if (element && !this.unobserve) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '300px' }
            );
        }

        /* When the fetchMore button appeared, we want to fetchMore if autoload is still active */
        const appeared = this.state.visible && !prevState.visible;
        if (this.state.autoLoadActive && appeared && this.fetchMore) {
            this.fetchMore();
            this.setState({ visible: false });
        }
    }

    renderHelmet = (tag: { name: string }) => (H: React.ComponentClass) => {
        const { site } = this.context;

        return (
            <FormattedMessage id='INSPIRATIONS'>
                {message => (
                    <H>
                        <title>
                            {tag ? `${tag.name} - ` : ''}
                            {message} | {site.name}
                        </title>
                    </H>
                )}
            </FormattedMessage>
        );
    };

    render() {
        const { styles, match, search } = this.props;
        const { slug } = match.params;
        const { offset } = search;

        let after: string | undefined;
        if (offset) {
            after = btoa(`arrayconnection:${parseInt(offset as any, 10) - 1}`);
        }

        return (
            <ViewerConsumer>
                {({ viewer }) => (
                    <>
                        <ScrollToTop offset={offset} slug={slug} />
                        <Query<InspirationQuery, InspirationQueryVariables>
                            query={query}
                            variables={{ slug, firstImages: FIRST, after, isAuth: Boolean(viewer) }}
                            fetchPolicy='cache-first'
                            notifyOnNetworkStatusChange={true}
                        >
                            {({ data, loading, error, fetchMore }) => {
                                const tagSlug = data?.tagSlugs?.edges.length && data.tagSlugs.edges[0]?.node;
                                const images = tagSlug && tagSlug.tag.images;

                                let endCursor: undefined | string | null;
                                if (images) {
                                    endCursor = images.pageInfo.endCursor;
                                }

                                const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
                                    endCursor,
                                    pathname: this.props.location.pathname,
                                    pageSize: FIRST,
                                });

                                let nextOffset;
                                if (endCursor) {
                                    nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
                                } else {
                                    nextOffset = offset;
                                }

                                const hasNextPage = images && images.pageInfo.hasNextPage;
                                this.fetchMore = hasNextPage
                                    ? () => {
                                          return fetchMore({
                                              variables: {
                                                  slug,
                                                  after: endCursor,
                                              },
                                              updateQuery: (
                                                  previousResult = {} as InspirationQuery,
                                                  { fetchMoreResult = {} as InspirationQuery }
                                              ) => {
                                                  return {
                                                      ...previousResult,
                                                      ...fetchMoreResult,
                                                      tagSlugs: {
                                                          ...(fetchMoreResult && fetchMoreResult.tagSlugs),
                                                          edges: [
                                                              {
                                                                  ...fetchMoreResult!.tagSlugs!.edges[0]!,
                                                                  node: {
                                                                      ...fetchMoreResult!.tagSlugs!.edges[0]!.node!,
                                                                      tag: {
                                                                          ...fetchMoreResult!.tagSlugs!.edges[0]!.node!
                                                                              .tag,
                                                                          images: mapAfter(
                                                                              previousResult!.tagSlugs!.edges[0]!.node!
                                                                                  .tag.images,
                                                                              fetchMoreResult!.tagSlugs!.edges[0]!.node!
                                                                                  .tag.images
                                                                          ),
                                                                      },
                                                                  },
                                                              },
                                                          ],
                                                      },
                                                  } as InspirationQuery;
                                              },
                                          }).then(result => {
                                              if (typeof window !== 'undefined') {
                                                  const {
                                                      location: { pathname },
                                                  } = this.props;
                                                  const [endCursorHref] = adjacentHrefsFromCursor({
                                                      endCursor,
                                                      pathname,
                                                      pageSize: FIRST,
                                                  });
                                                  gaTrack('pageview', endCursorHref);
                                              }
                                              return result;
                                          });
                                      }
                                    : undefined;
                                return (
                                    <LoadingProgress loading={loading}>
                                        <ErrorPage error={error}>
                                            <>
                                                {tagSlug !== 0 && tagSlug?.tag ? (
                                                    <>
                                                        <Helmet render={this.renderHelmet(tagSlug.tag)} />
                                                        <div className={styles.titleRow}>
                                                            <h1 className={styles.title}>{tagSlug.tag.name}</h1>
                                                            <InspirationInfo
                                                                id={tagSlug.tag.id}
                                                                imagesCount={tagSlug.tag.imagesCount}
                                                            />
                                                        </div>
                                                    </>
                                                ) : null}
                                                {images ? (
                                                    <>
                                                        <Masonry
                                                            edges={images.edges}
                                                            fetchMoreFn={this.fetchMore}
                                                            columnsSizes={columnsSizes}
                                                            disableLazyLoad={true}
                                                        />
                                                        <div className={styles.fetchMore} ref={this.ref}>
                                                            <FetchMoreButton
                                                                disabled={loading}
                                                                loading={loading}
                                                                onClick={() => {
                                                                    if (this.fetchMore) {
                                                                        this.fetchMore();
                                                                        if (!this.state.autoLoadActive) {
                                                                            this.setState({
                                                                                autoLoadActive: true,
                                                                                visible: false,
                                                                            });
                                                                        }
                                                                    }
                                                                }}
                                                                href={endCursorHref}
                                                                prevHref={prevCursorHref}
                                                            />
                                                            <OffsetPager
                                                                style={{ nav: styles.navOfOffsetPager }}
                                                                pageSize={FIRST}
                                                                offset={nextOffset}
                                                            />
                                                        </div>
                                                    </>
                                                ) : null}
                                                <Query<TagSlugTagSlugsQuery, TagSlugTagSlugsQueryVariables>
                                                    query={relatedContentQuery}
                                                    variables={{ id: (tagSlug as any)?.id }}
                                                    skip={!Boolean(tagSlug && tagSlug.id)}
                                                >
                                                    {result => (
                                                        <>
                                                            {result?.data?.tagSlugTagSlugs?.edges.length ? (
                                                                <div className={styles.tagSlugs}>
                                                                    <FormattedMessage id='INSPIRATION_RELATED_INSPIRATIONS'>
                                                                        {msg => (
                                                                            <h2 className={styles.sideHeader}>{msg}</h2>
                                                                        )}
                                                                    </FormattedMessage>
                                                                    {result.data.tagSlugTagSlugs.edges.map(edge => (
                                                                        <RelatedInspiration
                                                                            style={{ inspiration: styles.inspiration }}
                                                                            key={edge?.node?.id}
                                                                            inspiration={edge?.node!}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            ) : null}
                                                        </>
                                                    )}
                                                </Query>
                                            </>
                                        </ErrorPage>
                                    </LoadingProgress>
                                );
                            }}
                        </Query>
                    </>
                )}
            </ViewerConsumer>
        );
    }
}
export default Styled(defaultStyles)(Inspiration);
